import { Avatar, Group, Menu, Text, UnstyledButton, rem } from '@mantine/core';
import { IconBuilding, IconChevronDown, IconPlaneTilt, IconSchool } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { useSession } from '../app/session';
import type { OrganisationAccess } from '../api/types';

const KIND_ICONS = {
  CLUB: IconBuilding,
  FLIGHT_SCHOOL: IconSchool,
  AIRPORT: IconPlaneTilt,
} as const;

function KindIcon({ kind, size = 15 }: { kind: string; size?: number }) {
  const Icon = KIND_ICONS[kind as keyof typeof KIND_ICONS] ?? IconBuilding;
  return <Icon size={size} />;
}

function Logo({ organisation, size = 30 }: { organisation: OrganisationAccess; size?: number }) {
  return (
    <Avatar src={organisation.logo} radius="sm" size={size} color="aero">
      {organisation.short_name?.slice(0, 2).toUpperCase() ||
        organisation.name.slice(0, 2).toUpperCase()}
    </Avatar>
  );
}

/**
 * Switching club is the one action that changes the meaning of every other screen, so it sits
 * at the top of the sidebar with the club's own logo on it.
 */
export function OrganisationSwitcher() {
  const { t } = useTranslation();
  const { organisations, organisation, selectOrganisation } = useSession();

  if (!organisation) return null;

  const grouped = organisations.reduce<Record<string, OrganisationAccess[]>>((groups, entry) => {
    const key = entry.kind;
    (groups[key] ??= []).push(entry);
    return groups;
  }, {});

  const single = organisations.length === 1;

  const trigger = (
    <UnstyledButton
      w="100%"
      px="sm"
      py={8}
      style={{
        borderRadius: 'var(--mantine-radius-md)',
        border: `${rem(1)} solid var(--mantine-color-default-border)`,
      }}
    >
      <Group gap="sm" wrap="nowrap">
        <Logo organisation={organisation} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text fz="sm" fw={600} truncate="end">
            {organisation.short_name || organisation.name}
          </Text>
          <Text fz="xs" c="dimmed" truncate="end">
            {t(`organisation.${organisation.kind}`)}
          </Text>
        </div>
        {single ? null : <IconChevronDown size={15} opacity={0.6} />}
      </Group>
    </UnstyledButton>
  );

  if (single) return trigger;

  return (
    <Menu width="target" position="bottom-start" withinPortal shadow="md">
      <Menu.Target>{trigger}</Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>{t('common.switchOrganisation')}</Menu.Label>
        {Object.entries(grouped).map(([kind, entries]) => (
          <div key={kind}>
            {Object.keys(grouped).length > 1 ? (
              <Menu.Label>{t(`organisation.${kind}`)}</Menu.Label>
            ) : null}
            {entries.map((entry) => (
              <Menu.Item
                key={entry.id}
                leftSection={<KindIcon kind={entry.kind} />}
                onClick={() => void selectOrganisation(entry.slug)}
                bg={entry.id === organisation.id ? 'var(--mantine-color-default-hover)' : undefined}
              >
                <Text fz="sm" truncate="end">
                  {entry.name}
                </Text>
              </Menu.Item>
            ))}
          </div>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}
